import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useCommon } from '../../contexts/CommonContext';
import { useTenant } from '../../contexts/TenantContext';
import TableWithPagination from '../../components/TableWithPagination/TableWithPagination';
import PageHeader from '../../components/PageHeader/PageHeader';
import PageContainer from '../../components/PageContainer/PageContainer';
import EmptyState from '../../components/EmptyState/EmptyState';
import CreateCatalogModal from './CreateCatalogModal/CreateCatalogModal';
import { KnowledgeBaseApi } from '../../api/KnowledgeBaseApi';
import { showAlert, showToast } from '../../api/CommonApi';
import { CatalogUpdatedBy, MoreOptionsButton, SearchInput, StyledTableRow } from './styles';
import { Catalog } from '../../models/knowledgeBase/Catalog';
import {
  BdsButton,
  BdsDropdown,
  BdsGrid,
  BdsList,
  BdsListItem,
  BdsTableCell,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../hooks/useContentLocalizer';
import { usePaginatedSearch } from '../../hooks/usePaginatedSearch';
import { useTableStates } from '../../hooks/useTableStates';
import { useDateTimeFormatter } from '../../hooks/useDateTimeFormatter';
import { localization } from './localization';
import { calculateOffset } from './helper';
import { useLocalizedBreadcrumbs } from '../../utils/breadcrumbUtils';
import { trackEvent } from '../../api/Billing/Tracking';
import { KNOWLEDGE_CATALOG_OPEN, KNOWLEDGE_CATALOG_DELETE, action, origin } from '../../shared/SegmentEvents';
import { MetricStatus } from '../../shared/BillingMetrics';

// Constants
const PAGE_OPTIONS = [5, 10, 15];

type DeleteCatalogAlertResponse = { response: boolean };

const KnowledgeBase: React.FC = () => {
  const history = useHistory();
  const {
    setHeaderContent,
    common: { language, loggedUser },
  } = useCommon();
  const { tenant } = useTenant();
  const translate = useContentLocalizer(localization);

  // Pagination and search hook
  const {
    searchTerm,
    debouncedSearchTerm,
    page,
    itemsPerPage,
    loadingState,
    setLoadingState,
    handleSearchChange,
    handlePaginationChange,
    handleItemsPerPageChange,
  } = usePaginatedSearch({ pageOptions: PAGE_OPTIONS });

  // State management
  const [catalog, setCatalog] = useState<Catalog[]>([]);
  const [totalCatalogs, setTotalCatalogs] = useState(0);
  const [isCreateCatalogModalOpen, setIsCreateCatalogModalOpen] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  // Computed values
  const { isLoading, hasError } = loadingState;
  const tenantId = tenant?.info?.id;

  const computedStates = useTableStates({
    loadingState,
    itemsLength: catalog.length,
    searchTermLength: debouncedSearchTerm.length,
  });

  const formatDateTime = useDateTimeFormatter(language, translate.at);

  // API handlers
  const fetchCatalog = useCallback(async (): Promise<void> => {
    if (!tenantId || !itemsPerPage || !page) return;

    setLoadingState({ isLoading: true, hasError: false });
    try {
      const data = await KnowledgeBaseApi.getCatalogAsync(
        tenantId,
        itemsPerPage,
        calculateOffset(page, itemsPerPage),
        debouncedSearchTerm || undefined,
      );
      setCatalog(data.items);
      setTotalCatalogs(data.total);
      setLoadingState({ isLoading: false, hasError: false });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching catalog:', error);
      setLoadingState({ isLoading: false, hasError: true });
    }
  }, [tenantId, itemsPerPage, page, debouncedSearchTerm]);

  // Event handlers
  const handleModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsCreateCatalogModalOpen(false);
      if (!wasCancelled) fetchCatalog();
    },
    [fetchCatalog],
  );

  const handleCreateCatalog = useCallback((): void => setIsCreateCatalogModalOpen(true), []);
  const handleOpenCatalog = useCallback(
    (catalogId: string, catalogName: string): void => {
      const trackingData = {
        date: new Date(),
        planStatus: MetricStatus.normal,
        action: action.CLICK,
        origin: origin.KNOWLEDGEBASE,
      };

      trackEvent(KNOWLEDGE_CATALOG_OPEN, tenant, loggedUser, {
        ...trackingData,
        knowledgeName: catalogName,
      });

      history.push(`/knowledge-base/${catalogId}/documents`);
    },
    [history, tenant, loggedUser],
  );

  const showDeleteCatalogAlert = useCallback(
    (): Promise<DeleteCatalogAlertResponse> =>
      showAlert({
        ...translate.deleteCatalogAlert,
        variant: 'delete',
        icon: 'trash',
      }),
    [],
  );
  const handleDeleteCatalog = useCallback(
    async (catalogId: string) => {
      const confirm = await showDeleteCatalogAlert();
      if (!confirm.response) return;

      const catalogToDelete = catalog.find((item) => item.id === catalogId);
      const catalogName = catalogToDelete?.name ?? '';

      try {
        setLoadingState((prev) => ({ ...prev, isLoading: true }));
        await KnowledgeBaseApi.deleteCatalogAsync(tenant.info.id, catalogId);

        trackEvent(KNOWLEDGE_CATALOG_DELETE, tenant, loggedUser, {
          date: new Date(),
          planStatus: MetricStatus.normal,
          action: action.CLICK,
          origin: origin.KNOWLEDGEBASE,
          knowledgeName: catalogName,
        });

        showToast({
          ...translate.successDeleteCatalogToast,
          type: 'success',
        });
        fetchCatalog();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting catalog:', error);
        setLoadingState((prev) => ({ ...prev, isLoading: false }));
        showToast({
          ...translate.errorDeleteCatalogToast,
          type: 'danger',
        });
      }
    },
    [fetchCatalog, showDeleteCatalogAlert, tenant, loggedUser, catalog],
  );

  const handleDropdownOpen = useCallback((catalogId: string) => {
    setActiveDropdownId(catalogId);
  }, []);

  const handleDropdownClose = useCallback(() => {
    setActiveDropdownId(null);
  }, []);

  // Effects
  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const breadcrumbData = useLocalizedBreadcrumbs('knowledgeBase');

  useEffect(() => {
    if (breadcrumbData) {
      const { breadcrumbItems } = breadcrumbData;

      setHeaderContent({
        breadcrumbItems,
      });
    }
    return () => setHeaderContent({ redirect: null });
  }, [setHeaderContent, breadcrumbData]);

  // Render helpers
  const renderCatalogRow = useCallback(
    (item: Catalog) => {
      const isActive = activeDropdownId === item.id;
      return (
        <StyledTableRow key={item.id} isActive={isActive}>
          <BdsTableCell type="custom">
            <BdsTypo variant="fs-14" bold="bold">
              {item.name}
            </BdsTypo>
          </BdsTableCell>
          <BdsTableCell>
            <BdsTypo variant="fs-14">{item.content_count}</BdsTypo>
          </BdsTableCell>
          <BdsTableCell type="custom">
            <BdsGrid direction="column">
              <BdsTypo variant="fs-14">{formatDateTime(item.updated_at)}</BdsTypo>
              <CatalogUpdatedBy>
                <BdsTypo variant="fs-12">{`${translate.by} ${item.updated_by}`}</BdsTypo>
              </CatalogUpdatedBy>
            </BdsGrid>
          </BdsTableCell>
          <BdsTableCell type="custom">
            <BdsDropdown
              position="bottom-left"
              onBdsToggle={(event: CustomEvent) => {
                if (event.detail.value) {
                  handleDropdownOpen(item.id);
                } else {
                  handleDropdownClose();
                }
              }}
            >
              <div slot="dropdown-activator">
                <MoreOptionsButton name="more-options-vertical" />
              </div>
              <div slot="dropdown-content">
                <BdsList type-list="default">
                  <BdsListItem
                    onClick={() => handleOpenCatalog(item.id, item.name)}
                    text="Abrir"
                    clickable
                    icon="folder"
                  />
                  <BdsListItem onClick={() => handleDeleteCatalog(item.id)} text="Excluir" clickable icon="trash" />
                </BdsList>
              </div>
            </BdsDropdown>
          </BdsTableCell>
        </StyledTableRow>
      );
    },
    [
      handleDeleteCatalog,
      handleOpenCatalog,
      formatDateTime,
      translate,
      activeDropdownId,
      handleDropdownOpen,
      handleDropdownClose,
    ],
  );

  const renderEmptyState = useCallback(
    () => (
      <EmptyState
        {...translate.emptyState}
        primaryButton={{
          label: translate.createCatalogButton,
          onClick: handleCreateCatalog,
        }}
      />
    ),
    [handleCreateCatalog],
  );

  const renderTableContent = useCallback(
    () => (
      <BdsGrid direction="column" gap="4">
        <SearchInput
          onBdsChange={handleSearchChange}
          icon="search"
          placeholder={translate.searchPlaceholder}
          value={searchTerm}
        />
        <TableWithPagination
          options={{
            heading: [
              translate.tableHeaders.catalog,
              translate.tableHeaders.content,
              translate.tableHeaders.lastUpdate,
              '',
            ],
            columnWidths: ['auto', '8rem', '13rem', '3rem'],
            itemsPerPageList: PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: totalCatalogs,
            isLoading,
            hasLoadingError: hasError,
            hasEmptyState: computedStates.hasSearchResults,
          }}
          onRefreshData={fetchCatalog}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {catalog.length > 0 && catalog.map(renderCatalogRow)}
        </TableWithPagination>
      </BdsGrid>
    ),
    [
      handleSearchChange,
      searchTerm,
      itemsPerPage,
      totalCatalogs,
      isLoading,
      hasError,
      computedStates.hasSearchResults,
      fetchCatalog,
      handlePaginationChange,
      handleItemsPerPageChange,
      catalog,
      renderCatalogRow,
    ],
  );

  // Early return for missing tenant
  if (!tenantId) return null;

  return (
    <PageContainer>
      <BdsGrid direction="column" gap="4">
        <PageHeader title={translate.title} subtitle={translate.subtitle}>
          {!computedStates.hasNoItems && (
            <BdsButton onClick={handleCreateCatalog}>{translate.createCatalogButton}</BdsButton>
          )}
        </PageHeader>
        {computedStates.hasNoItems ? renderEmptyState() : renderTableContent()}
      </BdsGrid>
      <CreateCatalogModal isOpen={isCreateCatalogModalOpen} tenantId={tenantId} onClose={handleModalClose} />
    </PageContainer>
  );
};

export default KnowledgeBase;
